/**
 * THE APPS OF OURS, as HOSTS, per environment -- the one list two decisions read: which return addresses the single
 * sign-on hub hands a sign-in to (`registeredReturnOrigin`, `const/single-sign-on.ts`), and which origins a worker's
 * credentialed CORS admits (`ALLOWED_ORIGINS`, `const/urls-connect.ts`). Moved out of the sign-on leaf 2026-09-21 so
 * the CORS allowlist could read it without a cycle (the sign-on leaf reads `urls-connect` for the local dev host).
 *
 * AN EXACT LIST, never "any host on an apex we own": a quiet ask needs no consent, so every host admitted here can
 * obtain a login for every signed-in visitor just by being visited. Under a wildcard, one subdomain pointed at a third
 * party, or a dangling record somebody else claims, is that host -- and what points where is a DNS fact this tree
 * cannot see. Forgetting to list a new app fails SAFE: that app simply never asks the hub and its pages are refused
 * cross-origin, so the cost is a missing shared login, never a leaked one. The sibling suite pins this list against the
 * labels the wrangler manifests publish, so it cannot drift quietly either.
 *
 * TWO ABSENCES ARE DELIBERATE. THE HUB'S OWN APEX: it hands sign-ins to apps, never to itself (the CORS allowlist adds
 * the hub's origins itself, for the hub's own pages). And `ACTIVE_BRAND.domains.app`, the NATIVE DEEP-LINK target
 * (`deepLinkScheme`, `components/property/deep-link.ts`): no worker of ours answers on it, so no browser tab could
 * ever be there to redeem a grant.
 *
 * BUILT PER ENVIRONMENT, so the two never cross: a production hub handing a grant to a development host would put a
 * live credential in a build nobody hardens.
 */

import { ACTIVE_BRAND } from './brand-registry.ts';
import { apexHostnames, httpsOrigin } from './brand-hostnames.ts';
import { KENECTING_APP_HOSTNAMES } from './kenecting-identity.ts';
import { NOOR_APP_HOSTNAMES } from './noor-identity.ts';
import { FEDERATED_APP_HOSTNAMES } from './federated-identity.ts';
import { WRANGLER_ENV_KEYS } from './config-environments.ts';
import { FEDERATED_SSO_APP, KENECTING_SSO_APP, NOOR_SSO_APP, SSO_BRAND_APEX_APP, SSO_BRAND_APPS } from './single-sign-on-apps.ts';
import type { WranglerEnvKey } from './config-environments.ts';
import type { SsoAppIdentity } from '@federa/types/single-sign-on';

/** The production apex app also answers on `www`; development has no such host. */
const PRODUCTION_WWW_LABEL = 'www';

/** Every hostname of one environment paired with what the hub's box wears for it: the brand's apps by the fleet's host
 *  grammar (`apexHostnames`), and each product that owns an apex of its own. ONE table for both questions -- is this
 *  host ours, and whose box is it -- so a host cannot be admitted without an answer to the second. */
function registeredAppsByHost(env: WranglerEnvKey): ReadonlyMap<string, SsoAppIdentity> {
	const brandApex = ACTIVE_BRAND.domains.primary;
	const rows = new Map<string, SsoAppIdentity>();
	for (const app of SSO_BRAND_APPS) rows.set(apexHostnames(brandApex, app.label)[env], app.identity);
	if (env === WRANGLER_ENV_KEYS.PRODUCTION) rows.set(`${PRODUCTION_WWW_LABEL}.${brandApex}`, SSO_BRAND_APEX_APP);
	rows.set(KENECTING_APP_HOSTNAMES[env], KENECTING_SSO_APP);
	rows.set(NOOR_APP_HOSTNAMES[env], NOOR_SSO_APP);
	rows.set(FEDERATED_APP_HOSTNAMES[env], FEDERATED_SSO_APP);
	return rows;
}

const REGISTERED_APPS_BY_HOST: Readonly<Record<WranglerEnvKey, ReadonlyMap<string, SsoAppIdentity>>> = {
	[WRANGLER_ENV_KEYS.DEV]: registeredAppsByHost(WRANGLER_ENV_KEYS.DEV),
	[WRANGLER_ENV_KEYS.PRODUCTION]: registeredAppsByHost(WRANGLER_ENV_KEYS.PRODUCTION),
};

export const REGISTERED_APP_HOSTS: Readonly<Record<WranglerEnvKey, ReadonlySet<string>>> = {
	[WRANGLER_ENV_KEYS.DEV]: new Set(REGISTERED_APPS_BY_HOST[WRANGLER_ENV_KEYS.DEV].keys()),
	[WRANGLER_ENV_KEYS.PRODUCTION]: new Set(REGISTERED_APPS_BY_HOST[WRANGLER_ENV_KEYS.PRODUCTION].keys()),
};

/** What the hub's box wears for the app at `host` in this environment, or null for a host that is not one of ours.
 *  A `host`, port included, exactly as {@link REGISTERED_APP_HOSTS} lists it.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): reads this leaf's own `REGISTERED_APPS_BY_HOST`. */
export function registeredAppIdentity(host: string, env: WranglerEnvKey): SsoAppIdentity | null {
	return REGISTERED_APPS_BY_HOST[env].get(host) ?? null;
}

/**
 * The same hosts as ORIGINS -- what a CORS allowlist compares a request's `Origin` header against.
 * @runtime-derived-ssot-helper composes `httpsOrigin` over {@link REGISTERED_APP_HOSTS}; no second spelling.
 */
export function registeredAppOrigins(env: WranglerEnvKey): readonly string[] {
	return [...REGISTERED_APP_HOSTS[env]].map(httpsOrigin);
}
